/**
 * mobile-nav.js
 * Toggle .js-mobile-nav via .js-menu-trigger with a smooth open/close animation.
 * Assumes GSAP 3 is available globally as `gsap` (optional; falls back to no-animation).
 *
 * Behavior:
 * - Removes `.hidden` before opening (so it can animate)
 * - Animates opacity + translateY
 * - Adds `.hidden` after closing animation completes
 * - Adds `overflow-hidden` to <body> while open (prevents background scroll)
 * - Supports Esc to close and closes when clicking a link inside the nav
 * - Idempotent init + destroy (safe with Barba): re-init per container, no duplicate listeners
 */

(function () {
  const SELECTORS = {
    trigger: '.js-menu-trigger',
    nav: '.js-mobile-nav'
  };

  let listeners = [];
  let state = {
    trigger: null,
    nav: null,
    isOpen: false
  };

  function on(el, evt, fn, opts) {
    if (!el) return;
    el.addEventListener(evt, fn, opts);
    listeners.push([el, evt, fn, opts]);
  }

  function offAll() {
    listeners.forEach(([el, evt, fn, opts]) => {
      try { el.removeEventListener(evt, fn, opts); } catch (e) {}
    });
    listeners = [];
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function hasGSAP() {
    return typeof window.gsap !== 'undefined' && window.gsap && typeof window.gsap.to === 'function';
  }

  function setAria(expanded) {
    if (!state.trigger) return;
    state.trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function lockBody(lock) {
    document.body.classList.toggle('overflow-hidden', !!lock);
    document.body.classList.toggle('mobile-menu-open', !!lock);
  }

  function killNavTweens() {
    if (!hasGSAP() || !state.nav) return;
    window.gsap.killTweensOf(state.nav);
  }

  function openNav() {
    const { nav } = state;
    if (!nav) return;

    state.isOpen = true;

    // Make it renderable
    nav.classList.remove('hidden');
    setAria(true);
    lockBody(true);

    if (!hasGSAP() || prefersReducedMotion()) return;

    killNavTweens();
    window.gsap.set(nav, { opacity: 0, y: 0 });
    window.gsap.to(nav, {
      opacity: 1,
      y: 0,
      duration: 0.25,
      ease: 'power3.out',
      overwrite: 'auto'
    });
  }

  // 
  
  function closeNav() {
    const { nav } = state;
    if (!nav) return Promise.resolve();

    state.isOpen = false;
    setAria(false);
    lockBody(false);

    if (!hasGSAP() || prefersReducedMotion()) {
      nav.classList.add('hidden');
      return Promise.resolve();
    }

    killNavTweens();

    return new Promise((resolve) => {
      window.gsap.to(nav, {
        opacity: 0,
        y: 0,
        duration: 0.25,
        ease: 'power3.in',
        // overwrite: 'auto',
        onComplete: () => {
          nav.classList.add('hidden');
          window.gsap.set(nav, { clearProps: 'opacity,transform' });
          resolve();
        }
      });
    });
  }

  function closeNavImmediate() {
    const { nav } = state;
    if (!nav) return;

    state.isOpen = false;
    setAria(false);
    lockBody(false);

    killNavTweens();
    nav.classList.add('hidden');
    if (hasGSAP()) window.gsap.set(nav, { clearProps: 'opacity,transform' });
  }

  function toggleNav(e) {
    if (e) e.preventDefault();
    state.isOpen ? closeNav() : openNav();
  }

  function init(root) {
    root = root || document;

    // Cleanup previous init (idempotent)
    destroy();

    const trigger = document.querySelector(SELECTORS.trigger);
    const nav = document.querySelector(SELECTORS.nav);

    // If one is missing, do nothing
    if (!trigger || !nav) return;

    state.trigger = trigger;
    state.nav = nav;

    // Ensure starting state is consistent with class
    state.isOpen = !nav.classList.contains('hidden');
    setAria(state.isOpen);

    // Click trigger
    on(trigger, 'click', toggleNav);

    // ESC closes
    on(document, 'keydown', (e) => {
      if (e.key === 'Escape' && state.isOpen) closeNav();
    });

    // Click on a link inside nav closes (common UX)
    on(nav, 'click', (e) => {
      const a = e.target.closest('a');
      if (!a || !state.isOpen) return;

      const href = a.getAttribute('href') || '';
      if (!href || href.startsWith('#') || a.target === '_blank') return;

      // external? pusti normalno
      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;
      } catch (err) {}

      // preseci sve (Barba + browser)
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // ✅ odmah sakrij trenutni content (ispod menija)
      const current = document.querySelector('[data-barba="container"]');
      if (current) {
        if (hasGSAP() && !prefersReducedMotion()) {
          window.gsap.killTweensOf(current);
          window.gsap.to(current, { opacity: 0, duration: 0.12, overwrite: 'auto' });
        } else {
          current.style.opacity = '0';
        }
      }

      // flag da Barba zna da je "current" već sklonjen
      window.__navHoldOpen = true;

      // ✅ navigacija tek sad
      if (window.barba) window.barba.go(href);
      else window.location.href = href;
    }, true); // capture



    // Optional: click outside nav closes (only if nav is overlay/fullscreen)
    // Uncomment if you want:
    // on(document, 'click', (e) => {
    //   if (!state.isOpen) return;
    //   const clickedTrigger = e.target.closest(SELECTORS.trigger);
    //   const clickedNav = e.target.closest(SELECTORS.nav);
    //   if (!clickedTrigger && !clickedNav) closeNav();
    // });
  }

  function destroy() {
    // stop animations + unlock body
    if (state.isOpen) lockBody(false);
    killNavTweens();

    // remove listeners
    offAll();

    // clear refs
    state.trigger = null;
    state.nav = null;
    state.isOpen = false;
  }

  // Boot on first load
  function boot() {
    init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Barba support (if present)
  if (window.barba && window.barba.hooks && typeof window.barba.hooks.afterEnter === 'function') {
    window.barba.hooks.afterEnter((data) => {
      init(data.next.container);
    });

    // optional: ensure cleanup before leaving
    if (typeof window.barba.hooks.beforeLeave === 'function') {
      window.barba.hooks.beforeLeave(() => {
        if (window.__navHoldOpen) return;
        closeNavImmediate();
        destroy();
      });
    }
  }

  // Expose for manual control/debug
  window.MobileNav = { init, destroy, open: openNav, close: closeNav, toggle: toggleNav };
})();
